import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { Plus, Server, CheckCircle, Globe, Network, Shield } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '../../services/api';
import { HostCreateInputSchema, type HostCreateInput, type HostUpdateInput } from '@sbclite/core/schemas';
import type { Host } from '@sbclite/core';
import { useServerCertificatesForDropdown } from '../../hooks/useCertificates';
import { useAuth } from '../../contexts/AuthContext';
import { useHostEngineAssociations } from '../../hooks/useHostEngineAssociations';
import { useViewMode } from '../../hooks/useViewMode';
import { createFormErrorHandler } from '../../utils/form-error-handler';
import {
  PageHeader,
  PageContainer,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  Modal,
  DeleteConfirmModal,
  FormField,
  FormInput,
  FormSelect,
  FormCheckbox,
  type FilterOption,
} from '../../components/common';

type HostInfo = Host.HostInfo;

type HostFormData = HostCreateInput;

type FilterType = 'all' | 'default' | 'domain' | 'ip' | 'certificate';

type ViewMode = 'grid' | 'list';

const queryKey = ['hosts'];

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All SIP Identities' },
  { value: 'default', label: 'Default SIP Identity' },
  { value: 'domain', label: 'With Domain' },
  { value: 'ip', label: 'With External IP' },
  { value: 'certificate', label: 'With Stored Certificate' },
];

export default function HostsPage() {
  const queryClient = useQueryClient();
  const [editingHost, setEditingHost] = useState<HostInfo | null>(null);
  const [deleteHost, setDeleteHost] = useState<HostInfo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [viewMode, setViewMode] = useViewMode();

  const { data: hostsResponse, isLoading, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () => api.getHosts(),
  });

  const hosts = useMemo(() => hostsResponse?.data || [], [hostsResponse]);
  const refreshDependencies = async () => {
    await Promise.all(['hosts', 'host-usage', 'sip-peers', 'sip-interfaces', 'container-prerequisites', 'routing-rules'].map(key => queryClient.invalidateQueries({ queryKey: [key] })));
  };
  const mutationError = (error: unknown) => toast.error(error instanceof Error ? error.message : 'Host change failed');

  const stats = useMemo(() => {
    return {
      total: hosts.length,
      defaults: hosts.filter((host) => host.isDefault).length,
      withDomain: hosts.filter((host) => host.domain).length,
      withExternalIp: hosts.filter((host) => host.externalIp).length,
      withCertificate: hosts.filter((host) => host.certificateId).length,
    };
  }, [hosts]);

  const filteredHosts = useMemo(() => {
    const search = searchQuery.toLowerCase();

    return hosts
      .filter((host) => {
        const matchesSearch =
          host.name.toLowerCase().includes(search) ||
          (host.domain || '').toLowerCase().includes(search) ||
          (host.externalIp || '').toLowerCase().includes(search);

        if (!matchesSearch) return false;

        switch (filterType) {
          case 'default':
            return host.isDefault;
          case 'domain':
            return !!host.domain;
          case 'ip':
            return !!host.externalIp;
          case 'certificate':
            return !!host.certificateId;
          default:
            return true;
        }
      })
      .sort((a, b) => {
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.name.localeCompare(b.name);
      });
  }, [hosts, searchQuery, filterType]);

  const createMutation = useMutation({
    mutationFn: (data: HostFormData) => api.createHost(data),
    onSuccess: async () => {
      await refreshDependencies();
      setIsModalOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: HostUpdateInput }) => api.updateHost(id, data),
    onSuccess: async () => {
      await refreshDependencies();
      setIsModalOpen(false);
      setEditingHost(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteHost(id),
    onSuccess: async () => {
      await refreshDependencies();
      setDeleteHost(null);
    },
    onError: mutationError,
  });

  const handleCreate = () => {
    setEditingHost(null);
    setIsModalOpen(true);
  };

  const handleEdit = (host: HostInfo) => {
    setEditingHost(host);
    setIsModalOpen(true);
  };

  const handleSetDefault = async (host: HostInfo) => {
    try { await updateMutation.mutateAsync({ id: host.id, data: { isDefault: true } }); }
    catch (error) { mutationError(error); }
  };
  const busy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingState message="Loading hosts..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorState message={error.message} onRetry={() => void refetch()} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="SIP Identities"
        description="Manage public-facing SIP identities using configured domain and external IP values"
        onRefresh={() => void refreshDependencies()}
        isRefreshing={isFetching}
        primaryAction={{
          label: 'Add SIP Identity',
          icon: Plus,
          onClick: handleCreate,
          disabled: busy,
        }}
      />

      <p className="text-sm text-gray-600">Domain/IP values provide SIP identity and can define authentication realms. Saved values do not verify DNS, reachability or TLS. Runtime API/web Docker containers are not managed here; review SIP Engines for engine observations. The first identity becomes default automatically; deleting an unused default selects the first remaining identity by name. Change the default explicitly to choose a replacement.</p>

      <StatsGrid>
        <StatCard
          label="Total SIP Identities"
          value={stats.total}
          icon={Server}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
          valueColor="text-blue-600"
        />
        <StatCard
          label="Default"
          value={stats.defaults}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Domains"
          value={stats.withDomain}
          icon={Globe}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />
        <StatCard
          label="External IPs"
          value={stats.withExternalIp}
          icon={Network}
          iconBgColor="bg-amber-100"
          iconColor="text-amber-600"
          valueColor="text-amber-600"
        />
        <StatCard
          label="Certificates"
          value={stats.withCertificate}
          icon={Shield}
          iconBgColor="bg-indigo-100"
          iconColor="text-indigo-600"
          valueColor="text-indigo-600"
        />
      </StatsGrid>

      <FilterBar
        searchPlaceholder="Search hosts..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={FILTER_OPTIONS}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode as ViewMode}
        onViewModeChange={(mode) => setViewMode(mode as ViewMode)}
      />

      {filteredHosts.length === 0 ? (
        <EmptyState
          icon={Server}
          title="No SIP identities found"
          description="Create a SIP identity to define domain/IP configuration. TLS listener certificates are selected through SIP interfaces and TLS contexts."
          isFiltered={searchQuery !== '' || filterType !== 'all'}
          action={{ label: 'Add SIP Identity', icon: Plus, onClick: handleCreate }}
        />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredHosts.map((host) => (
            <ItemCard
              key={host.id}
              title={host.name}
              subtitle={host.domain || host.externalIp || 'No domain/IP'}
              icon={<ItemCardIcon icon={<Server className="w-4 h-4" />} bgColor="bg-blue-100" />}
              status={
                host.isDefault ? (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    Default
                  </span>
                ) : undefined
              }
              details={[
                { label: 'Domain', value: host.domain || '-' },
                { label: 'External IP', value: host.externalIp || '-' },
                { label: 'Certificate association', value: host.certificateId ? 'Stored (not listener TLS)' : 'None' },
              ]}
              actions={
                <ItemActions
                  variant="card"
                  actions={[
                    { kind: 'edit', onClick: () => handleEdit(host), disabled: busy },
                    { kind: 'delete', onClick: () => setDeleteHost(host), disabled: busy },
                    {
                      kind: 'setDefault',
                      label: host.isDefault ? 'Default' : 'Set Default',
                      onClick: () => handleSetDefault(host),
                      disabled: host.isDefault || busy,
                    },
                  ]}
                />
              }
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Host</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">External IP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Certificate</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredHosts.map((host) => (
                <tr key={host.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">{host.name}</div>
                      {host.isDefault && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1 w-fit">
                          Default
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{host.domain || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{host.externalIp || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {host.certificateId ? 'Stored (not listener TLS)' : 'None'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <ItemActions
                      variant="table"
                      actions={[
                        { kind: 'edit', onClick: () => handleEdit(host), disabled: busy },
                        { kind: 'delete', onClick: () => setDeleteHost(host), disabled: busy },
                        {
                          kind: 'setDefault',
                          label: host.isDefault ? 'Default' : 'Set Default',
                          onClick: () => handleSetDefault(host),
                          disabled: host.isDefault || busy,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <HostModal
        isOpen={isModalOpen}
        editingHost={editingHost}
        onClose={() => {
          if (busy) return;
          setIsModalOpen(false);
          setEditingHost(null);
        }}
        onSubmit={async (data) => {
          if (editingHost) {
            await updateMutation.mutateAsync({ id: editingHost.id, data });
          } else {
            await createMutation.mutateAsync(data);
          }
        }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      <DeleteConfirmModal
        isOpen={!!deleteHost}
        onClose={() => { if (!busy) setDeleteHost(null); }}
        onConfirm={async () => {
          if (deleteHost) {
            try { await deleteMutation.mutateAsync(deleteHost.id); } catch { /* Mutation reports the actionable error; retain dialog. */ }
          }
        }}
        title="Delete SIP Identity"
        entityType="SIP identity"
        entityName={deleteHost?.name || ''}
        additionalInfo={deleteHost && <HostUsageSummary hostId={deleteHost.id} />}
        warningMessage="Only unused hosts can be deleted. Deleting the default selects the first remaining host by name."
        isDeleting={deleteMutation.isPending}
      />
    </PageContainer>
  );
}

function HostUsageSummary({ hostId }: { hostId: string }) {
  const { user } = useAuth();
  const usage = useQuery({
    queryKey: ['host-usage', hostId],
    queryFn: async () => (await api.get<{ success: boolean; data: Host.HostUsage }>(`/api/hosts/${encodeURIComponent(hostId)}/usage`)).data.data,
  });
  const engines = useHostEngineAssociations(hostId, user?.id, user?.role === 'admin');
  if (usage.isLoading) return <p className="text-sm">Loading linked resources…</p>;
  if (usage.error) return <ErrorState message={usage.error.message} onRetry={() => void usage.refetch()} />;
  if (!usage.data) return null;
  return <div className="space-y-1 text-xs text-gray-600">
    <p>Linked SIP interfaces: {usage.data.interfaces.map(item => item.name).join(', ') || 'None'}</p>
    <p>Linked peers: {usage.data.peers.map(item => item.name).join(', ') || 'None'}</p>
    <p>Linked routes: {usage.data.routes.map(item => item.name).join(', ') || 'None'}</p>
    <p>Authentication records: {usage.data.authenticationCount}; credentials: {usage.data.credentialCount}.</p>
    {user?.role === 'admin' && <div className="border-t pt-2 mt-2">
      <p className="font-medium text-gray-700">Saved SIP engine associations</p>
      {engines.isLoading ? <p>Loading engine associations…</p> : engines.error ? <p className="text-amber-800">Engine associations unavailable: {engines.error instanceof Error ? engines.error.message : 'Request failed'}</p> : engines.data ? <>
        {engines.data.engines.length === 0 ? <p>No saved engine associations.</p> : <ul className="space-y-1">{engines.data.engines.map((engine) => <li key={`${engine.containerId}:${engine.profileId}`}><Link className="text-blue-700" to={`/system/containers/${encodeURIComponent(engine.containerId)}`}>{engine.containerId}</Link> · {engine.profileName} · saved profile/interface link{engine.registryPresent ? ' · registry record exists' : ' · no registry row at lookup'}</li>)}</ul>}
        {engines.data.truncated && <p className="text-amber-800">Some engine associations are omitted; the result is truncated.</p>}
        <p className="mt-1">These are saved associations, not proof of engine state, deployment, or runtime application.</p>
      </> : null}
    </div>}
    <p>Linked resources must be reassigned before deletion. Realm changes are blocked while digest credentials exist; existing hashes cannot be reused with a new realm.</p>
  </div>;
}

function HostModal({
  isOpen,
  editingHost,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  isOpen: boolean;
  editingHost: HostInfo | null;
  onClose: () => void;
  onSubmit: (data: HostFormData) => Promise<void>;
  isSubmitting: boolean;
}) {
  const formErrorHandler = createFormErrorHandler({
    componentName: 'HostModal',
    showFieldErrors: true,
  });

  const form = useForm<HostFormData>({
    resolver: zodResolver(HostCreateInputSchema),
    defaultValues: {
      name: editingHost?.name || '',
      domain: editingHost?.domain || null,
      externalIp: editingHost?.externalIp || null,
      certificateId: editingHost?.certificateId || null,
      isDefault: editingHost?.isDefault || false,
    },
  });

  React.useEffect(() => {
    if (!isOpen) return;
    form.reset({
      name: editingHost?.name || '',
      domain: editingHost?.domain || null,
      externalIp: editingHost?.externalIp || null,
      certificateId: editingHost?.certificateId || null,
      isDefault: editingHost?.isDefault || false,
    });
  }, [isOpen, editingHost, form]);

  const { data: serverCertificates, isLoading: serverCertsLoading, error: certificateError, refetch: refetchCertificates } = useServerCertificatesForDropdown();

  const handleSubmit = async (data: HostFormData) => {
    try {
      await onSubmit(data);
    } catch (error) {
      formErrorHandler.onError(error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!isSubmitting) onClose(); }}
      title={editingHost ? 'Edit SIP Identity' : 'Create SIP Identity'}
      subtitle="Define public-facing identity for Via/Contact headers"
      size="lg"
    >
      <form onSubmit={form.handleSubmit(handleSubmit, formErrorHandler.onInvalid)}>
        <fieldset disabled={isSubmitting} className="space-y-6">
          {editingHost && <HostUsageSummary hostId={editingHost.id} />}
          <FormField label="Name" required error={form.formState.errors.name?.message}>
            <FormInput {...form.register('name')} placeholder="e.g., SBC Primary" />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Domain"
              error={form.formState.errors.domain?.message}
              hint="Hostname only, without scheme, port or path. Used for SIP identity and authentication realms."
            >
              <FormInput {...form.register('domain')} placeholder="e.g., sip.example.com" />
            </FormField>

            <FormField
              label="External IP"
              error={form.formState.errors.externalIp?.message}
              hint="Public IP address (if no domain)"
            >
              <FormInput {...form.register('externalIp')} placeholder="e.g., 203.0.113.50" />
            </FormField>
          </div>

          <p className="text-xs text-gray-500">
            At least one of Domain or External IP is required for SIP routing.
          </p>

            <FormField label="Stored identity certificate association" hint="Stored association only. Current engine TLS listeners use the certificate attached to their SIP interface’s TLS context.">
            {certificateError && <ErrorState message={certificateError.message} onRetry={() => void refetchCertificates()} />}
            <FormSelect {...form.register('certificateId')} disabled={serverCertsLoading}>
              <option value="">Select certificate (optional)</option>
              {editingHost?.certificateId && !serverCertificates?.some(cert => cert.value === editingHost.certificateId) && <option value={editingHost.certificateId}>Existing association: {editingHost.certificateId} (not in current inventory)</option>}
              {(serverCertificates || []).map((cert) => (
                <option key={cert.value} value={cert.value}>
                  {cert.label}
                </option>
              ))}
            </FormSelect>
          </FormField>
          <Link className="text-sm text-blue-700" to="/foundation/tls-contexts" target="_blank" rel="noopener noreferrer">Manage TLS contexts (new tab)</Link>

          <FormCheckbox {...form.register('isDefault')} label="Set as default host" />
        </fieldset>

        <div className="flex items-center justify-end gap-3 mt-6">
          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save SIP Identity'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
